// ═══════════════════════════════════════════════════════════════
// PIXALLOY — Kernel Integration
// ═══════════════════════════════════════════════════════════════
// Bridges PixAlloy's WasmBridge (9 C kernels, 6.6KB wasm binary)
// into the framework's KernelRegistry. This unifies the kernel
// dispatch so applyAdjustment and engine filters route through
// KernelRegistry.run() instead of checking B._wasmAvailable.
//
// Usage:
//   import { registerPixAlloyKernels } from './kernel-integration.js';
//   const registry = bridge.kernels; // or new KernelRegistry()
//   registerPixAlloyKernels(registry, wasmBridge);
//   registry.run("invert", data, w, h);
// ═══════════════════════════════════════════════════════════════

/**
 * Register all PixAlloy kernels into a KernelRegistry.
 *
 * Each kernel gets both a Wasm export name (used when WasmBridge._wasmAvailable)
 * and a JS fallback compute function (used otherwise). The KernelRegistry
 * tries Wasm first, falls through to JS if the export isn't found or fails.
 *
 * @param registry - The framework's KernelRegistry instance
 * @param wasmBridge - PixAlloy's WasmBridge instance (after init())
 */
export function registerPixAlloyKernels(registry, wasmBridge) {
  // Wrap the WASM bridge to ensure correct per-kernel parameter ordering.
  // The framework's WasmKernelBridge.runKernel passes params sorted alphabetically,
  // but C kernels expect a specific positional order. This wrapper uses
  // runPixelKernel (which takes explicit trailing args) for correct ordering.
  const isAvailable = () => {
    if (typeof wasmBridge.available === 'boolean') return wasmBridge.available;
    return !!wasmBridge._wasmAvailable;
  };

  // Per-kernel param ordering: maps kernel name → function that extracts args in C order
  const PARAM_ORDER = {
    invert: () => [],
    grayscale: () => [],
    sepia: () => [],
    brightness_contrast: (p) => [p?.brightness ?? 0, p?.contrast ?? 0],
    levels: (p) => [p?.inBlack ?? 0, p?.inWhite ?? 255, p?.gamma ?? 1.0, p?.outBlack ?? 0, p?.outWhite ?? 255],
    hue_saturation: (p) => [p?.hue ?? 0, p?.saturation ?? 0, p?.lightness ?? 0],
    // sharpen and gaussian_blur need scratch buffers — handled via runPixelKernelWithScratch
  };

  registry.setWasmRunner({
    get available() { return isAvailable(); },
    runKernel(name, data, w, h, params) {
      if (!isAvailable()) return false;
      // Use runPixelKernel for kernels with known param ordering
      if (PARAM_ORDER[name] && typeof wasmBridge.runPixelKernel === 'function') {
        try {
          wasmBridge.runPixelKernel(name, data, ...PARAM_ORDER[name](params));
          return true;
        } catch { return false; }
      }
      // Sharpen: needs scratch buffer (data, scratch, w, h)
      if (name === 'sharpen' && typeof wasmBridge.runPixelKernelWithScratch === 'function') {
        try {
          wasmBridge.runPixelKernelWithScratch(name, data, data.length, w, h);
          return true;
        } catch { return false; }
      }
      // Legacy WasmBridge fallback (handles per-kernel dispatch internally)
      if (wasmBridge._wasmAvailable && typeof wasmBridge.runKernel === 'function') {
        try { wasmBridge.runKernel(name, data, w, h, params || {}); return true; }
        catch { return false; }
      }
      return false;
    }
  });

  // ── Register kernels with JS fallbacks ──

  registry.register({
    name: "invert",
    wasmExport: "invert",
    js(data, w, h) {
      const d = data, len = d.length;
      for (let i = 0; i < len; i += 4) { d[i] = 255 - d[i]; d[i + 1] = 255 - d[i + 1]; d[i + 2] = 255 - d[i + 2]; }
    }
  });

  registry.register({
    name: "grayscale",
    wasmExport: "grayscale",
    js(data, w, h) {
      const d = data, len = d.length;
      for (let i = 0; i < len; i += 4) { const v = d[i] * 0.3 + d[i + 1] * 0.59 + d[i + 2] * 0.11; d[i] = d[i + 1] = d[i + 2] = v; }
    }
  });

  registry.register({
    name: "sepia",
    wasmExport: "sepia",
    js(data, w, h) {
      const d = data, len = d.length;
      for (let i = 0; i < len; i += 4) {
        const r = d[i], g = d[i + 1], b = d[i + 2];
        d[i] = Math.min(255, r * 0.393 + g * 0.769 + b * 0.189);
        d[i + 1] = Math.min(255, r * 0.349 + g * 0.686 + b * 0.168);
        d[i + 2] = Math.min(255, r * 0.272 + g * 0.534 + b * 0.131);
      }
    }
  });

  registry.register({
    name: "brightness_contrast",
    wasmExport: "brightness_contrast",
    js(data, w, h, params) {
      const d = data, len = d.length;
      const br = (params?.brightness || 0) / 100 * 255 * 0.5;
      const ct = (params?.contrast || 0) / 100;
      const factor = (1 + ct) * (1 + ct);
      const lut = new Uint8Array(256);
      for (let i = 0; i < 256; i++) { let v = ((i + br) / 255 - 0.5) * factor + 0.5; lut[i] = v < 0 ? 0 : v > 1 ? 255 : (v * 255 + 0.5) | 0; }
      for (let i = 0; i < len; i += 4) { d[i] = lut[d[i]]; d[i + 1] = lut[d[i + 1]]; d[i + 2] = lut[d[i + 2]]; }
    }
  });

  registry.register({
    name: "levels",
    wasmExport: "levels",
    js(data, w, h, params) {
      const d = data, len = d.length;
      const iB = params?.inBlack || 0, iW = params?.inWhite || 255, gm = params?.gamma || 1, oB = params?.outBlack || 0, oW = params?.outWhite || 255;
      const range = iW - iB || 1;
      const lut = new Uint8Array(256);
      for (let i = 0; i < 256; i++) { let v = (i - iB) / range; v = v < 0 ? 0 : v > 1 ? 1 : v; v = Math.pow(v, 1 / gm); lut[i] = (oB + v * (oW - oB) + 0.5) | 0; }
      for (let i = 0; i < len; i += 4) { d[i] = lut[d[i]]; d[i + 1] = lut[d[i + 1]]; d[i + 2] = lut[d[i + 2]]; }
    }
  });

  registry.register({
    name: "hue_saturation",
    wasmExport: "hue_saturation",
    js(data, w, h, params) {
      const d = data, len = d.length;
      const hShift = (params?.hue || 0) / 360, sScale = 1 + (params?.saturation || 0) / 100, lShift = (params?.lightness || 0) / 100 * 0.5;
      for (let i = 0; i < len; i += 4) {
        if (d[i + 3] < 1) continue;
        let r = d[i] / 255, g = d[i + 1] / 255, b = d[i + 2] / 255;
        const mx = Math.max(r, g, b), mn = Math.min(r, g, b), dl = mx - mn;
        let h2 = 0, s2 = 0, l2 = (mx + mn) / 2;
        if (dl > 0) { s2 = l2 > 0.5 ? dl / (2 - mx - mn) : dl / (mx + mn); if (mx === r) h2 = ((g - b) / dl + (g < b ? 6 : 0)) / 6; else if (mx === g) h2 = ((b - r) / dl + 2) / 6; else h2 = ((r - g) / dl + 4) / 6; }
        h2 = (h2 + hShift) % 1; if (h2 < 0) h2 += 1;
        s2 = Math.max(0, Math.min(1, s2 * sScale));
        l2 = Math.max(0, Math.min(1, l2 + lShift));
        if (s2 === 0) { r = g = b = l2; } else {
          const hue2rgb = (pp, q, t) => { if (t < 0) t += 1; if (t > 1) t -= 1; if (t < 1 / 6) return pp + (q - pp) * 6 * t; if (t < 1 / 2) return q; if (t < 2 / 3) return pp + (q - pp) * (2 / 3 - t) * 6; return pp; };
          const q2 = l2 < 0.5 ? l2 * (1 + s2) : l2 + s2 - l2 * s2, p2 = 2 * l2 - q2;
          r = hue2rgb(p2, q2, h2 + 1 / 3); g = hue2rgb(p2, q2, h2); b = hue2rgb(p2, q2, h2 - 1 / 3);
        }
        d[i] = (r * 255 + 0.5) | 0; d[i + 1] = (g * 255 + 0.5) | 0; d[i + 2] = (b * 255 + 0.5) | 0;
      }
    }
  });

  registry.register({
    name: "sharpen",
    wasmExport: "sharpen",
    js(data, w, h) {
      const d = data, src = new Uint8ClampedArray(d), k = [0, -1, 0, -1, 5, -1, 0, -1, 0];
      for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) for (let c = 0; c < 3; c++) {
        let v = 0; for (let ky = -1; ky <= 1; ky++) for (let kx = -1; kx <= 1; kx++) v += src[((y + ky) * w + (x + kx)) * 4 + c] * k[(ky + 1) * 3 + (kx + 1)];
        d[(y * w + x) * 4 + c] = Math.min(255, Math.max(0, v));
      }
    }
  });

  registry.register({
    name: "gaussian_blur",
    wasmExport: "gaussian_blur",
    js(data, w, h, params) {
      const d = data;
      const r = Math.max(1, Math.min(100, Math.round(params?.radius || 5))), sigma = r / 3;
      const kSize = r * 2 + 1; const kernel = new Float32Array(kSize); let kSum = 0;
      for (let i = -r; i <= r; i++) { const wt = Math.exp(-(i * i) / (2 * sigma * sigma)); kernel[i + r] = wt; kSum += wt; }
      for (let i = 0; i < kSize; i++) kernel[i] /= kSum;
      const tmp = new Float32Array(w * h * 4);
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) { let rr = 0, gg = 0, bb = 0, aa = 0; for (let k = -r; k <= r; k++) { const nx = Math.max(0, Math.min(w - 1, x + k)); const si = (y * w + nx) * 4; const wt = kernel[k + r]; rr += d[si] * wt; gg += d[si + 1] * wt; bb += d[si + 2] * wt; aa += d[si + 3] * wt; } const di = (y * w + x) * 4; tmp[di] = rr; tmp[di + 1] = gg; tmp[di + 2] = bb; tmp[di + 3] = aa; }
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) { let rr = 0, gg = 0, bb = 0, aa = 0; for (let k = -r; k <= r; k++) { const ny = Math.max(0, Math.min(h - 1, y + k)); const si = (ny * w + x) * 4; const wt = kernel[k + r]; rr += tmp[si] * wt; gg += tmp[si + 1] * wt; bb += tmp[si + 2] * wt; aa += tmp[si + 3] * wt; } const di = (y * w + x) * 4; d[di] = (rr + 0.5) | 0; d[di + 1] = (gg + 0.5) | 0; d[di + 2] = (bb + 0.5) | 0; d[di + 3] = (aa + 0.5) | 0; }
    }
  });

  registry.register({
    name: "apply_lut",
    js(data, w, h, params) {
      const d = data, len = d.length;
      const lut = params?.lut, ch = params?.channel || "master";
      if (!lut) return;
      if (ch === "master") { for (let i = 0; i < len; i += 4) { if (d[i + 3] < 1) continue; d[i] = lut[d[i]]; d[i + 1] = lut[d[i + 1]]; d[i + 2] = lut[d[i + 2]]; } }
      else if (ch === "r") { for (let i = 0; i < len; i += 4) d[i] = lut[d[i]]; }
      else if (ch === "g") { for (let i = 0; i < len; i += 4) d[i + 1] = lut[d[i + 1]]; }
      else if (ch === "b") { for (let i = 0; i < len; i += 4) d[i + 2] = lut[d[i + 2]]; }
    }
  });
}
